import { useEffect, useState, type FormEvent } from 'react';
import QRCode from 'qrcode';
import { useSesion } from '../sesion';
import { CampoContrasena, Error } from '../componentes/comunes';
import { requisitosDe } from '../contrasena';

type Etapa = 'credenciales' | 'mfa' | 'alta_mfa' | 'cambio';

/**
 * Acceso en tres etapas: credenciales, y luego —si la cuenta lo exige—
 * segundo factor o cambio de la contraseña temporal. Los 16 usuarios que
 * se migraron del Excel entran por la tercera, porque su contraseña vino
 * marcada como temporal.
 */
export default function Acceso() {
  const sesion = useSesion();

  const [etapa, setEtapa] = useState<Etapa>('credenciales');
  const [correo, setCorreo] = useState('');
  const [password, setPassword] = useState('');
  const [codigo, setCodigo] = useState('');
  const [nueva, setNueva] = useState('');
  const [repetida, setRepetida] = useState('');
  const [urlMfa, setUrlMfa] = useState('');
  const [tokenParcial, setTokenParcial] = useState('');
  const [secretoMfa, setSecretoMfa] = useState('');
  const [qr, setQr] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  // El código QR se dibuja en el navegador: el secreto no tiene por qué
  // pasar por un servicio externo para convertirse en imagen.
  useEffect(() => {
    if (!urlMfa) { setQr(''); return; }
    QRCode.toDataURL(urlMfa, { margin: 1, width: 220 })
      .then(setQr)
      .catch(() => setQr(''));
  }, [urlMfa]);

  function continuarSegun(r: { estado: string; tokenParcial?: string; secretoMfa?: string; urlMfa?: string }) {
    setCodigo('');
    if (r.estado === 'requiere_mfa') {
      setTokenParcial(r.tokenParcial ?? '');
      setEtapa('mfa');
    } else if (r.estado === 'requiere_alta_mfa') {
      setTokenParcial(r.tokenParcial ?? '');
      setSecretoMfa(r.secretoMfa ?? '');
      setUrlMfa(r.urlMfa ?? '');
      setEtapa('alta_mfa');
    } else if (r.estado === 'requiere_cambio_password') {
      setTokenParcial(r.tokenParcial ?? '');
      setEtapa('cambio');
    }
  }

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setTrabajando(true);

    try {
      if (etapa === 'credenciales') {
        continuarSegun(await sesion.acceder(correo, password));
      } else if (etapa === 'mfa') {
        continuarSegun(await sesion.verificarMfa(tokenParcial, codigo));
      } else if (etapa === 'alta_mfa') {
        continuarSegun(await sesion.altaMfa(tokenParcial, codigo));
      } else {
        if (nueva !== repetida) {
          throw new globalThis.Error('Las dos contraseñas no coinciden.');
        }
        await sesion.cambioInicial(tokenParcial, nueva);
      }
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <div className="acceso">
      <form className="caja panel" onSubmit={enviar}>
        <div className="marca">
          <img src="/logo.png" alt="APRISA · Mantenimiento industrial" />
        </div>

        {error != null && (
          error instanceof globalThis.Error && !('estado' in error)
            ? <div className="aviso error">{error.message}</div>
            : <Error error={error} />
        )}

        {etapa === 'credenciales' && (
          <>
            <div className="campo">
              <label htmlFor="correo">Correo</label>
              <input
                id="correo" type="email" value={correo} autoComplete="username" required
                onChange={(e) => setCorreo(e.target.value)}
              />
            </div>
            <CampoContrasena
              id="password"
              etiqueta="Contraseña"
              valor={password}
              onCambio={setPassword}
              autoComplete="current-password"
              requerido
            />
          </>
        )}

        {etapa === 'mfa' && (
          <div className="campo">
            <label htmlFor="codigo">Código de tu aplicación de autenticación</label>
            <input
              id="codigo" inputMode="numeric" pattern="\d{6}" maxLength={6} required
              value={codigo} onChange={(e) => setCodigo(e.target.value)}
              autoComplete="one-time-code"
            />
            <span className="ayuda">Seis dígitos.</span>
          </div>
        )}

        {etapa === 'alta_mfa' && (
          <>
            <div className="aviso">
              Tu perfil exige verificación en dos pasos. Escanea este código
              con Google Authenticator, Microsoft Authenticator o 1Password
              y escribe el número que aparezca.
            </div>

            {qr && (
              <img
                src={qr}
                alt="Código QR para dar de alta el autenticador"
                style={{ justifySelf: 'center', borderRadius: 8 }}
              />
            )}

            <div className="campo">
              <label htmlFor="secreto">Si no puedes escanear, escribe esta clave</label>
              <input id="secreto" className="mono" value={secretoMfa} readOnly />
            </div>

            <div className="campo">
              <label htmlFor="codigo">Código que muestra la aplicación</label>
              <input
                id="codigo" inputMode="numeric" pattern="\d{6}" maxLength={6} required
                value={codigo} onChange={(e) => setCodigo(e.target.value)}
                autoComplete="one-time-code"
              />
              <span className="ayuda">
                Si el código no es aceptado, revisa que la hora del teléfono
                esté en automático: el cálculo depende del reloj.
              </span>
            </div>
          </>
        )}

        {etapa === 'cambio' && (
          <>
            <div className="aviso">
              Tu contraseña es temporal. Elige una nueva para continuar.
            </div>
            <CampoContrasena
              id="nueva"
              etiqueta="Nueva contraseña"
              valor={nueva}
              onCambio={setNueva}
              autoComplete="new-password"
              requerido
              ayuda={
                /* Los siete requisitos, marcándose mientras se escribe.
                   La pantalla decía sólo «mínimo 12 caracteres» y la API
                   exige seis cosas más: se cumplía la única que estaba
                   escrita y el rechazo llegaba sin explicar cuál faltaba. */
                <ul className="requisitos">
                  {requisitosDe(nueva, correo).map((r) => (
                    <li key={r.texto} className={r.cumple ? 'cumple' : undefined}>
                      {r.cumple ? '✓' : '·'} {r.texto}
                      {r.texto.startsWith('Al menos') && nueva.length > 0 && nueva.length < 12
                        && ` — llevas ${nueva.length}`}
                    </li>
                  ))}
                  <li className={repetida.length > 0 && nueva === repetida ? 'cumple' : undefined}>
                    {repetida.length > 0 && nueva === repetida ? '✓' : '·'} Las dos iguales
                  </li>
                </ul>
              }
            />
            <CampoContrasena
              id="repetida"
              etiqueta="Repite la contraseña"
              valor={repetida}
              onCambio={setRepetida}
              autoComplete="new-password"
              requerido
            />
          </>
        )}

        <button className="primario" type="submit" disabled={trabajando}>
          {trabajando ? 'Un momento…' : etapa === 'credenciales' ? 'Entrar' : 'Continuar'}
        </button>
      </form>
    </div>
  );
}
